import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { errorMessage } from '@/api/errors';
import type { components } from '@/api/qraft/schema';
import { AlertBox } from '@/common/AlertBox';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { WBSButton } from '@/common/WBSButton';
import { WBSCheckbox } from '@/common/WBSCheckbox';
import { FollowUpPartnerPicker } from '@/follow-up/FollowUpPartnerPicker';
import {
  SectionHeading,
  ViewBody,
  ViewContainer,
  ViewHeader,
  ViewHeaderTitle,
} from '@/follow-up/followUpStyles';
import { StudentLocationNotice } from '@/follow-up/StudentLocationNotice';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { PageContainer } from '~/styles/boardStyles';
import { formatString } from '~/utils/stringFormat';

type FollowUpPartnerOption = components['schemas']['FollowUpPartnerOption'];

export const Route = createFileRoute('/_app/follow-ups/entries/convert')({
  component: ConvertRequestScreen,
  // `convert.php:11` is `$_GET['request']`, and both entry points pass it: the
  // coordinator queue's *Assign to Partner* action and the board's *Take*
  // success redirect (W4.5's DIV-7, discharged by this route).
  validateSearch: (search): { request?: number } => ({
    request: search.request ? Number(search.request) : undefined,
  }),
});

/**
 * Convert — `groups/single/follow-up-coordinator-group/follow-ups/entries/
 * convert.php` (137 lines), the centre of this group.
 *
 * A coordinator who has claimed a request from the board picks a follow-up
 * partner near the student, writes them a message, and presses Assign. That
 * write creates the `follow_ups` row: a point-in-time copy of the request's
 * three snapshots plus an INVITATION to one partner
 * (`requested_partner_id` set, `partner_id` deliberately NULL until they
 * accept), and moves the request to `in_progress`.
 *
 * THE OBJECTIVE LIST IS DISPLAY, NOT INPUT (L9). Legacy posts
 * `requesting_for[]` and then throws it away — `class-follow-up.php` re-reads
 * the request's own `selected_requests` — so the boxes are rendered checked and
 * disabled, exactly as legacy renders them `readonly checked`, and the POST
 * carries three fields, not seven.
 *
 * The *Search partners by location* link legacy puts under the picker
 * (`:44-53`) is not built: it opens the by-location map popup, which applies no
 * distance filter at all despite its own error text quoting a radius
 * (`search-follow-up-partners-popup.php:4-8`, radius helpers commented out) and
 * calls the identical country/language/unverified predicate the picker already
 * uses — it is a rendering of a set the picker returns, not a second query
 * (spec §10.3). The picker's own empty state replaces it.
 */
function ConvertRequestScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { request: requestId } = Route.useSearch();
  const { me, isPending: mePending } = useCurrentUser();

  const [partner, setPartner] = useState<FollowUpPartnerOption | null>(null);
  const [message, setMessage] = useState('');

  const {
    data: draft,
    isLoading,
    isError,
  } = api.getFollowUpConvertDraft.useQuery(
    { path: { request_id: requestId ?? 0 } },
    { enabled: requestId !== undefined },
  );

  const {
    mutateAsync: createFollowUp,
    isPending: assigning,
    error: assignError,
  } = api.createFollowUp.useMutation();

  const handleAssign = async (): Promise<void> => {
    if (requestId === undefined || partner === null) return;
    let created: components['schemas']['FollowUpConversionResult'];
    try {
      created = await createFollowUp({
        body: {
          request_id: requestId,
          partner_id: partner.id,
          message_for_partner: message,
        },
      });
    } catch {
      // Rendered from `assignError` — 403 for a request another coordinator
      // adopted or a partner outside the picker set, 409 for a request that is
      // no longer `adopted` or already has a follow-up.
      return;
    }
    // The request leaves the coordinator's queue and the new follow-up joins
    // their entries list.
    await Promise.all([
      api.listCoordinatorPendingFollowUpRequests.invalidateQueries(),
      api.listCoordinatorFollowUps.invalidateQueries(),
      api.listFollowUpRequestBoard.invalidateQueries(),
    ]);
    // `convert.php:28`'s `redirect_to` is `follow-ups/entries/view/`, with the
    // new id appended by the handler (`class-follow-up.php:243`).
    await navigate({
      to: '/follow-ups/entries/view/$followUpId/',
      params: { followUpId: String(created.id) },
    });
  };

  // ADMIN deliberately absent — DIV-2. `create_follow_up` and the draft read
  // both require the coordinator who ADOPTED the request, and an admin has no
  // row in `followup_coordinators`.
  if (mePending || isLoading) return null;
  if (me?.profile_type !== 'FOLLOWUP_COORDINATOR') return <NotFoundScreen />;
  // 404, not 403, for a request outside the caller's scope, so the read is not
  // an existence oracle for the other 75 coordinators' rows.
  if (requestId === undefined || isError || draft === undefined) {
    return <NotFoundScreen />;
  }

  return (
    <PageContainer>
      <ViewContainer>
        <ViewHeader>
          <ViewHeaderTitle>{t('wbs.new-follow-up')}</ViewHeaderTitle>
        </ViewHeader>
        <ViewBody>
          <SectionHeading>
            {formatString(
              t('wbs.create-follow-up-from-request'),
              String(draft.request_id),
            )}
          </SectionHeading>

          <SectionHeading>
            {t('lessons.target-student')}{' '}
            {draft.student !== null && (
              <StudentLink>
                <Link
                  to="/members/$memberId/profile/"
                  params={{ memberId: String(draft.student.id) }}
                >
                  {draft.student.display_name}
                </Link>
              </StudentLink>
            )}
          </SectionHeading>

          <Section>
            <SectionHeading>{t('wbs.follow-up-partner')}</SectionHeading>
            {/* The three D18 axes — the request's country CODE (DIV-6), the
                student's real snapshot language (DIV-7, normalised by DIV-5)
                and `unverified = 0 OR created_by = me` — are all resolved
                server-side from `request_id`, so nothing is passed from here
                that a caller could tamper with. */}
            <FollowUpPartnerPicker
              requestId={draft.request_id}
              value={partner}
              onChange={setPartner}
            />
          </Section>

          <Section>
            <SectionHeading>{t('wbs.objective')}</SectionHeading>
            <ObjectiveList>
              {draft.selected_requests.map((option) => (
                <ObjectiveRow key={option.value}>
                  <WBSCheckbox checked disabled readOnly onChange={() => {}} />
                  <span>{option.label}</span>
                </ObjectiveRow>
              ))}
            </ObjectiveList>
          </Section>

          <Section>
            <SectionHeading>
              {t('wbs.message-to-follow-up-partner')}
            </SectionHeading>
            <MessageBox
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              rows={5}
              data-testid="message-for-partner"
            />
          </Section>

          {/* §10.3 — the map is not built. `convert.php:110-113` appends the
              student's address in bold and that half IS built, because the
              address is already on the draft. */}
          <StudentLocationNotice address={draft.student_contact_info.address} />

          {assignError != null && (
            <AlertBox variant="error">
              {errorMessage(
                assignError,
                t('wbs.there-was-a-problem-with-your-submission'),
              )}
            </AlertBox>
          )}

          <Actions>
            {/* `convert.php:97` targets `follow-ups/follow-ups`, a path that
                does not exist; both subnavs fall back to pending-requests, so
                Cancel lands on the queue. Measured live on both group tabs. */}
            <WBSButton
              alternate
              as={Link}
              to="/follow-ups/pending-requests/"
              data-testid="cancel-convert"
            >
              {t('actions.cancel')}
            </WBSButton>
            {/* Disabled until a partner is picked, where the reassign dialog
                posts nothing and renders published copy instead. The asymmetry
                is legacy's: `FollowUpPartnerInteractivePopup.php:44-46` has an
                empty-selection message (unit 125370, 4/4) and convert has none
                at all — its handler just reads `$_POST['follow_up_partner_id']`
                and would write a follow-up naming nobody. */}
            <WBSButton
              type="button"
              onClick={() => void handleAssign()}
              isLoading={assigning}
              disabled={partner === null}
              data-testid="assign-follow-up-partner"
            >
              {t('wbs.assign')}
            </WBSButton>
          </Actions>
        </ViewBody>
      </ViewContainer>
    </PageContainer>
  );
}

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

/** A wrapper rather than `styled(Link)`: wrapping the component erases its
 *  route-typed `to`/`params` props, so the compiler stops checking the target. */
const StudentLink = styled.span`
  a {
    color: var(--wbs-red);
    text-decoration: none;
  }
`;

const ObjectiveList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ObjectiveRow = styled.label`
  align-items: center;
  display: flex;
  font-size: 14px;
  gap: 10px;
`;

const MessageBox = styled.textarea`
  background: var(--grey-100);
  border: none;
  border-radius: 6px;
  box-shadow: var(--shadow-inset-md);
  font-family: inherit;
  font-size: 14px;
  line-height: 20px;
  outline: none;
  padding: 12px 14px;
  resize: vertical;
  width: 100%;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;
